import { useState, useRef, useEffect } from "react"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { COMM_FIELD_BASE } from "@/lib/styles/companyEditTabShell"
import type { MultiDropdownOption, MultiDropdownProps } from "./MultiDropdown.types"

export type { MultiDropdownOption, MultiDropdownProps } from "./MultiDropdown.types"

function normalizeOptions(options: MultiDropdownOption[]): { label: string; value: string }[] {
  return options.map((o) => (typeof o === "string" ? { label: o, value: o } : o))
}

function slugForTestId(s: string): string {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "item"
}

/**
 * Multi-select control aligned with Angular company-edit “select2”-style chips (Preferences → Property Mgmt System).
 */
export function MultiDropdown({
  options,
  selected,
  onChange,
  placeholder,
  disabled,
  triggerDataTestId,
  chipRemoveTestIdPrefix,
}: MultiDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const pairs = normalizeOptions(options)
  const labelByValue = new Map(pairs.map((p) => [p.value, p.label]))

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const toggleOption = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  return (
    <div className="relative" ref={ref}>
      <div
        data-testid={triggerDataTestId}
        className={cn(
          // Match Communications resident-paid row height when empty; grow with wrapped chips (no fixed h-10).
          "flex w-full min-w-0 min-h-10 h-auto items-center justify-between",
          COMM_FIELD_BASE,
          disabled
            ? "cursor-not-allowed bg-slate-50 opacity-60"
            : "cursor-pointer hover:border-[rgb(21_151_255)]"
        )}
        onClick={(e) => {
          if (disabled) return
          e.stopPropagation()
          setIsOpen((open) => !open)
        }}
      >
        <div className="flex min-h-8 flex-1 flex-wrap items-center gap-1">
          {selected.length === 0 ? (
            <span className="text-sm font-medium text-slate-600">{placeholder}</span>
          ) : (
            selected.map((val) => {
              const label = labelByValue.get(val) ?? val
              return (
                <span
                  key={val}
                  className="flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-px text-xs text-slate-700"
                >
                  {label}
                  <button
                    type="button"
                    data-testid={`${chipRemoveTestIdPrefix}-${slugForTestId(val)}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleOption(val)
                    }}
                    className="ml-1 font-bold hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              )
            })
          )}
        </div>
        <ChevronDown size={14} className="ml-2 shrink-0 text-slate-400" aria-hidden />
      </div>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
          onMouseDown={(e) => {
            e.preventDefault()
          }}
        >
          {pairs.map((opt, idx) => {
            const isSelected = selected.includes(opt.value)
            return (
              <div
                key={`${opt.value}-${idx}`}
                className={`flex cursor-pointer items-center justify-between px-4 py-2 text-sm ${
                  isSelected
                    ? "bg-[rgba(21,151,255,0.08)] text-[rgb(21_151_255)]"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleOption(opt.value)
                }}
              >
                <span>{opt.label}</span>
                {isSelected && <Check size={16} className="text-[rgb(21_151_255)]" aria-hidden />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
